import { useState, useEffect } from "react";
import { TextField } from "../form-fields/TextField";

export default function BrandKitTab({ initialData = {} }) {
  const [companyName, setCompanyName] = useState("");
  const [companyDescription, setCompanyDescription] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");
  const [companyColors, setCompanyColors] = useState([]);
  const [companyTone, setCompanyTone] = useState("");

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (initialData) {
      setCompanyName(initialData.companyName || "");
      setCompanyDescription(initialData.companyDescription || "");
      setCompanyLogo(initialData.companyLogo || "");
      
      let colors = initialData.companyColors || [];
      if (typeof colors === 'string') {
        colors = colors.split(',').map(c => c.trim()).filter(Boolean);
      }
      setCompanyColors(Array.isArray(colors) ? colors : []);
      
      setCompanyTone(initialData.companyTone || "");
      setIsLoading(false);
    }
  }, [initialData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-gray-600">Loading brand kit details...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="">
        <div className="py-5 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Brand Kit</h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage your brand identity, styling, and company details
          </p>
        </div>
        <div className="py-5 space-y-6">
          <TextField
            label="Company Name"
            type="text"
            id="companyName"
            value={companyName}
            disabled
            placeholder="Enter your company name"
          />

          <div className="flex flex-col gap-4">
            <label className="text-charcoal-700 flex items-center gap-2 text-sm leading-none font-medium select-none">
              Company Logo
            </label>
            {companyLogo ? (
              <img 
                src={companyLogo} 
                alt="Company Logo" 
                className="h-16 w-auto max-w-[200px] object-contain" 
              />
            ) : (
              <div className="h-16 w-16 bg-gray-100 rounded-md flex items-center justify-center text-gray-400 text-xs">
                No Logo
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <label
              htmlFor="companyDescription"
              className="text-charcoal-700 flex items-center gap-2 text-sm leading-none font-medium select-none"
            >
              Company Description
            </label>
            <textarea
              id="companyDescription"
              value={companyDescription}
              disabled
              placeholder="Describe your business..."
              rows={4}
              className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input flex w-full min-w-0 rounded-md border border-gray-200 bg-gray-50 px-3 py-3 text-base transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
            />
          </div>

          <div>
            <label className="text-charcoal-700 flex items-center gap-2 text-sm leading-none font-medium select-none mb-4">
              Brand Colors
            </label>
            <div className="flex flex-wrap gap-4 items-center">
              {companyColors.map((color, idx) => (
                <div key={idx} className="relative group flex items-center gap-2">
                  <div className="relative">
                    <div
                      className="w-10 h-10 border border-gray-200 rounded-lg overflow-hidden"
                      style={{ backgroundColor: color }}
                    />
                  </div>
                  <input
                    type="text"
                    value={color}
                    disabled
                    className="w-24 text-sm px-2 py-1.5 border border-gray-200 rounded-md uppercase bg-gray-50 text-gray-500 outline-none cursor-not-allowed"
                  />
                </div>
              ))}
              {companyColors.length === 0 && (
                <span className="text-sm text-gray-400">No brand colors</span>
              )}
            </div>
          </div>

          <TextField
            label="Tone of Voice"
            type="text"
            id="companyTone"
            value={companyTone}
            disabled
            placeholder="Warm, Friendly and Approachable"
          />
        </div>
      </div>
    </div>
  );
}
